import { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Outlet, Route as R, Routes, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useSession } from './session';
import { MCPER_APP_BASE, McperRoute } from './routes';
import { McperShell } from './McperShell';
import { LoginPage } from './pages/LoginPage';
import { mcperService } from './mcper.service';
import './mcper.css';

/* Pages load on demand; the first paint ships the shell and the sign-in page. */
const OverviewPage = lazy(() => import('./pages/OverviewPage').then((m) => ({ default: m.OverviewPage })));
const SetupPage = lazy(() => import('./pages/SetupPage').then((m) => ({ default: m.SetupPage })));
const SitesPage = lazy(() => import('./pages/SitesPage').then((m) => ({ default: m.SitesPage })));
const SignInsPage = lazy(() => import('./pages/SignInsPage').then((m) => ({ default: m.SignInsPage })));
const SignInSitePage = lazy(() => import('./pages/SignInSitePage').then((m) => ({ default: m.SignInSitePage })));
const AgentsPage = lazy(() => import('./pages/AgentsPage').then((m) => ({ default: m.AgentsPage })));
const CallsPage = lazy(() => import('./pages/CallsPage').then((m) => ({ default: m.CallsPage })));
const SettingsPage = lazy(() => import('./pages/SettingsPage').then((m) => ({ default: m.SettingsPage })));
const AuthorizePage = lazy(() => import('./pages/AuthorizePage').then((m) => ({ default: m.AuthorizePage })));

/**
 * The mcper.io app: one URL for the whole web, a person's dashboard behind it.
 */
export function McperApp() {
  return (
    <BrowserRouter basename={MCPER_APP_BASE}>
      <Suspense fallback={null}>
        <Routes>
          <R path={McperRoute.Login} element={<LoginPage />} />
          <R path={McperRoute.SignedIn} element={<SignedInPage />} />
          <R element={<RequireSignIn />}>
            {/* Opened by an agent: one focused task, outside the dashboard frame. */}
            <R path={McperRoute.SignInSite} element={<SignInSitePage />} />
            <R path={McperRoute.Authorize} element={<AuthorizePage />} />
            <R element={<McperShell />}>
              <R path={McperRoute.Overview} element={<OverviewPage />} />
              <R path={McperRoute.Setup} element={<SetupPage />} />
              <R path={McperRoute.Sites} element={<SitesPage />} />
              <R path={McperRoute.SignIns} element={<SignInsPage />} />
              <R path={McperRoute.Agents} element={<AgentsPage />} />
              <R path={McperRoute.Calls} element={<CallsPage />} />
              <R path={McperRoute.Settings} element={<SettingsPage />} />
            </R>
          </R>
          <R path="*" element={<Navigate to={McperRoute.Overview} replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

/** Private routes: with a token but no person yet, loads them once; without a token, the sign-in page. */
function RequireSignIn() {
  const token = useSession((s) => s.accessToken);
  const person = useSession((s) => s.person);
  const setSession = useSession((s) => s.setSession);
  const clear = useSession((s) => s.clear);
  const [checking, setChecking] = useState(Boolean(token && !person));
  const location = useLocation();

  useEffect(() => {
    if (token && !person) {
      mcperService
        .me()
        .then((p) => setSession(token, p))
        .catch(() => clear())
        .finally(() => setChecking(false));
    }
  }, [token, person, setSession, clear]);

  if (!token) {
    const wanted = `${location.pathname}${location.search}`;
    return <Navigate to={location.pathname === McperRoute.Overview ? McperRoute.Login : `${McperRoute.Login}?redirect=${encodeURIComponent(wanted)}`} replace />;
  }
  if (checking) return null;
  return <Outlet />;
}

/** After the provider: the server set the session cookie and sent the browser to /signed-in?next=<path>; trade it for a token. */
function SignedInPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const setSession = useSession((s) => s.setSession);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    mcperService
      .refresh()
      .then((r) => {
        setSession(r.accessToken, r.person);
        navigate(safePath(params.get('next')), { replace: true });
      })
      .catch(() => setFailed(true));
  }, [setSession, navigate, params]);

  if (failed) return <Navigate to={McperRoute.Login} replace />;
  return null;
}

/** Only a path of this app: never another origin, never the sign-in pages themselves. */
export function safePath(next: string | null): string {
  if (!next || !next.startsWith('/') || next.startsWith('//') || next.startsWith(McperRoute.SignedIn) || next.startsWith(McperRoute.Login)) return McperRoute.Overview;
  return next;
}
